import React from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Alert,
  Grid,
  TextField,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { LEAD_FIELDS, TRANSFORM_OPTIONS } from "../../../constants/automationEvents";

const TemplateStep = ({
  formData,
  templates,
  previewVariables,
  onTemplateChange,
  onAddParameter,
  onParameterChange,
  onRemoveParameter,
}) => {
  

  return (
    <Box sx={{ pt: 2 }}>
      {/* Template Selector */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          fontSize: "0.68rem",
          letterSpacing: 1.2,
          color: "text.secondary",
          mb: 0.75,
          display: "block",
        }}
      >
        Select WhatsApp Template <span style={{ color: "#d32f2f" }}>*</span>
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={formData.action.channel.whatsapp?.templateName || ""}
          onChange={(e) => onTemplateChange(e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>
                  Choose a template...
                </Typography>
              );
            }
            const template = templates.find((t) => t.name === selected);
            if (!template) return selected;
            return (
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.88rem", fontWeight: 500 }}
                  >
                    {template.name}
                  </Typography>
                  {template.status && (
                    <Chip
                      label={template.status}
                      size="small"
                      color={
                        template.status === "APPROVED" || template.status === "ACTIVE"
                          ? "success"
                          : template.status === "REJECTED"
                            ? "error"
                            : "default"
                      }
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3, mt: 0.25, fontSize: "0.7rem" }}
                >
                  {template.category} &bull; {template.language}
                </Typography>
              </Box>
            );
          }}
          sx={{
            borderRadius: 1.5,
            bgcolor: "#F8FAFC",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.200" },
            "& .MuiSelect-select": {
              py: formData.action.channel.whatsapp?.templateName ? 1.2 : 1.5,
            },
          }}
        >
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.name} sx={{ py: 1.2 }}>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                    {template.name}
                  </Typography>
                  {template.status && template.status !== "ACTIVE" && (
                    <Chip
                      label={template.status}
                      size="small"
                      sx={{ height: 18, fontSize: "0.58rem", fontWeight: 600 }}
                      color={
                        template.status === "APPROVED"
                          ? "success"
                          : template.status === "REJECTED"
                            ? "error"
                            : "default"
                      }
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {template.category} &bull; {template.language}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Template Preview */}
      {formData.action.channel.whatsapp?.templateName && (
        <Accordion
          sx={{
            mt: 2,
            boxShadow: "none",
            border: "1.5px dashed",
            borderColor: "primary.main",
            borderRadius: "8px !important",
            "&::before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
            sx={{
              minHeight: 42,
              px: 2,
              "& .MuiAccordionSummary-content": { my: 0.8 },
            }}
          >
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "text.secondary" }}>
              Template Preview
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1.5,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                fontSize: "0.8rem",
                lineHeight: 1.6,
              }}
            >
              {templates.find(
                (t) => t.name === formData.action.channel.whatsapp?.templateName
              )?.preview || "Template preview not available"}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Parameter Mapping Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        mb={1.5}
      >
        <Typography
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "text.secondary",
            fontSize: "0.7rem",
          }}
        >
          Map Template Variables to Lead Fields
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
          onClick={onAddParameter}
          disabled={previewVariables.length === 0}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.7rem",
            letterSpacing: 0.5,
          }}
        >
          Add Parameter
        </Button>
      </Box>

      {previewVariables.length > 0 && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            fontSize: "0.8rem",
            py: 0.5,
            bgcolor: "#F0F4FF",
            "& .MuiAlert-icon": { fontSize: 18 },
            "& .MuiAlert-message": { fontSize: "0.8rem" },
          }}
        >
          This template requires {previewVariables.length} variable
          {previewVariables.length > 1 ? "s" : ""}:{" "}
          <strong>{previewVariables.map((v) => v.placeholder).join(", ")}</strong>
        </Alert>
      )}

      {formData.action.parameters.length === 0 && previewVariables.length === 0 && (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ fontSize: "0.82rem", textAlign: "center", py: 3 }}
        >
          Select a template to see variable mappings.
        </Typography>
      )}

      {/* Parameter Rows */}
      {formData.action.parameters.map((param, index) => (
        <Box
          key={index}
          mb={2}
          sx={{
            p: 2,
            bgcolor: "#FAFBFC",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          {/* Variable Label */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.78rem",
              color: "text.primary",
              mb: 1.5,
              letterSpacing: 0.3,
            }}
          >
            {param.templateVariable}
          </Typography>

          {/* Field Labels */}
          <Grid container spacing={1.5} sx={{ mb: 0.5 }}>
            <Grid size={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "text.secondary",
                  fontSize: "0.62rem",
                  letterSpacing: 1,
                }}
              >
                Lead Field
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "text.secondary",
                  fontSize: "0.62rem",
                  letterSpacing: 1,
                }}
              >
                Transform
              </Typography>
            </Grid>
            <Grid size={4}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "text.secondary",
                  fontSize: "0.62rem",
                  letterSpacing: 1,
                }}
              >
                Default Value
              </Typography>
            </Grid>
          </Grid>

          {/* Inputs */}
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={param.leadField}
                  onChange={(e) =>
                    onParameterChange(index, "leadField", e.target.value)
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return "Select Field";
                    const field = LEAD_FIELDS.find((f) => f.value === selected);
                    return field
                      ? `${field.category} → ${field.label}`
                      : selected;
                  }}
                  sx={{
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                    bgcolor: "#F8FAFC",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.200" },
                  }}
                >
                  {LEAD_FIELDS.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      <Typography variant="caption" sx={{ fontSize: "0.78rem" }}>
                        {field.category} &rarr; {field.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={param.transform || ""}
                  onChange={(e) =>
                    onParameterChange(index, "transform", e.target.value)
                  }
                  sx={{
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                    bgcolor: "#F8FAFC",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.200" },
                  }}
                >
                  {TRANSFORM_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Default Value"
                value={param.defaultValue || ""}
                onChange={(e) =>
                  onParameterChange(index, "defaultValue", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                    bgcolor: "#F8FAFC",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.200" },
                  },
                }}
              />
            </Grid>
            <Grid size={1} sx={{ display: "flex", justifyContent: "center" }}>
              <IconButton
                size="small"
                onClick={() => onRemoveParameter(index)}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default TemplateStep;
